/**
 * Descarga del CSV de una exportación.
 *
 * Por qué esto no pasa por `useApiQuery`
 * --------------------------------------
 * `GET /api/v1/exports/{id}/download` no devuelve JSON: devuelve un archivo
 * (`text/csv`, servido con `Content-Encoding: gzip`). `ApiClient.parse` hace
 * `response.json()` y sobre un CSV tira `malformed_response`, así que el
 * cliente tipado no puede traerlo — y `client.ts` no es un archivo de este
 * módulo para ir a agregarle un verbo.
 *
 * La regla del andamio es "nada de `useEffect` + `fetch`", y sigue en pie: es
 * sobre **estado de servidor**, que es lo que se pierde al saltearse TanStack
 * Query. Esto no es estado: es un efecto disparado por un click que produce un
 * blob y termina. No hay nada que cachear ni que invalidar.
 *
 * Lo que sí hay que respetar es el token. La llamada previa a
 * `GET /exports/{id}` no es decorativa: pasa por el `ApiClient`, que refresca
 * el access token si está por vencer. Sin eso, una pestaña abierta hace una
 * hora bajaría el archivo con un token muerto y el usuario vería un 401 sobre
 * un botón que "antes andaba".
 *
 * Y confirma contra el servidor que el trabajo sigue en `done` antes de gastar
 * la descarga — que queda registrada en `audit_log`: quién exportó qué y
 * cuándo. El backend lo hace solo; acá simplemente no se lo elude.
 */
import type { ApiClient } from '@/api/client';
import { ApiError, asApiError } from '@/api/errors';
import type { Schemas } from '@/api/types';

export type ExportJob = Schemas['services__api__routers__exports__ExportOut'];

/** Nombre sugerido si la respuesta no trae `Content-Disposition`. */
export function fallbackFilename(job: ExportJob): string {
  return `xyvona-${job.kind}-${job.id}.csv`;
}

/** Lee el nombre del `Content-Disposition`, si lo hay. */
export function filenameFromHeader(header: string | null): string | null {
  if (header === null) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  const raw = match?.[1];
  if (raw === undefined) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export interface DownloadDeps {
  api: ApiClient;
  fetchImpl?: typeof fetch;
  /** Sólo para tests: el `document` sobre el que se dispara el click. */
  doc?: Document;
}

/**
 * Baja el archivo y lo entrega al navegador. Devuelve el nombre usado.
 *
 * Sin `window.open` ni un `<a href>` directo: la ruta exige `Authorization`, y
 * una navegación del navegador no manda el bearer. Tampoco se pone el token en
 * la URL — terminaría en el historial y en el log de acceso del proxy.
 */
export async function downloadExport(job: ExportJob, deps: DownloadDeps): Promise<string> {
  const { api, fetchImpl = api.fetcher, doc = document } = deps;

  // Refresca el token si hace falta y revalida el estado del trabajo.
  const fresh = await api.get<ExportJob>(`/api/v1/exports/${job.id}`);
  if (fresh.status !== 'done') {
    throw new ApiError({
      status: 409,
      code: 'export_not_ready',
      message: `La exportación está en estado '${fresh.status}'.`,
    });
  }

  const session = api.currentSession();
  if (session === null) {
    throw new ApiError({ status: 401, code: 'no_session', message: 'No hay sesión iniciada.' });
  }

  let response: Response;
  try {
    // Mismo defecto que tenía el QR de la ficha (#140): sin el prefijo del
    // ambiente, la descarga cae en verify y contesta 404.
    response = await fetchImpl(api.resolve(`/api/v1/exports/${job.id}/download`), {
      headers: { Authorization: `Bearer ${session.accessToken}` },
      credentials: 'omit',
      redirect: 'error',
    });
  } catch (cause) {
    throw asApiError(cause);
  }

  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: response.status === 404 ? 'not_found' : 'export_download_failed',
      message: 'No se pudo descargar el archivo.',
      traceId: response.headers.get('X-Trace-Id'),
    });
  }

  const name =
    filenameFromHeader(response.headers.get('Content-Disposition')) ?? fallbackFilename(fresh);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = doc.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.rel = 'noopener';
    doc.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Sin esto el blob queda vivo hasta que se cierre la pestaña, y un CSV de
    // unidades son decenas de MB por descarga.
    URL.revokeObjectURL(url);
  }
  return name;
}
